import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EnvironmentService } from '../../../../core/environment/environment.service';

export interface PreferenceChannelState {
  channel: string;
  enabled: boolean;
  configurable: boolean;
}

export interface PreferenceEventState {
  event_type: string;
  label: string;
  condition: string;
  hidden?: boolean;
  channels: PreferenceChannelState[];
}

interface ApiResponse<T> {
  data: T;
  error: null | string;
  status: string;
}

@Injectable({ providedIn: 'root' })
export class NotificationPreferencesService {
  private readonly http = inject(HttpClient);
  private readonly envService = inject(EnvironmentService);

  private get baseUrl(): string {
    return `${this.envService.apiUrls.notificationEngine}/v2/preferences`;
  }

  getPreferences(): Observable<{ events: PreferenceEventState[] }> {
    return this.http
      .get<ApiResponse<{ email: string; events: PreferenceEventState[] }>>(this.baseUrl)
      .pipe(map(res => ({ events: res.data.events ?? [] })));
  }

  updatePreference(eventType: string, channel: string, enabled: boolean): Observable<void> {
    return this.http.patch<void>(this.baseUrl, { event_type: eventType, channel, enabled });
  }

  selectAll(): Observable<{ events: PreferenceEventState[] }> {
    return this.http
      .post<ApiResponse<{ email: string; events: PreferenceEventState[] }>>(`${this.baseUrl}/select-all`, {})
      .pipe(map(res => ({ events: res.data.events ?? [] })));
  }

  deselectAll(): Observable<{ events: PreferenceEventState[] }> {
    return this.http
      .post<ApiResponse<{ email: string; events: PreferenceEventState[] }>>(`${this.baseUrl}/deselect-all`, {})
      .pipe(map(res => ({ events: res.data.events ?? [] })));
  }

  selectAllForChannel(channel: string): Observable<{ events: PreferenceEventState[] }> {
    return this.http
      .post<ApiResponse<{ email: string; events: PreferenceEventState[] }>>(`${this.baseUrl}/select-all/${channel}`, {})
      .pipe(map(res => ({ events: res.data.events ?? [] })));
  }

  deselectAllForChannel(channel: string): Observable<{ events: PreferenceEventState[] }> {
    return this.http
      .post<ApiResponse<{ email: string; events: PreferenceEventState[] }>>(`${this.baseUrl}/deselect-all/${channel}`, {})
      .pipe(map(res => ({ events: res.data.events ?? [] })));
  }
}
